import { EmailActionModal, type EmailActionPrefill } from "@/components/mirror/EmailActionModal";
import type { ModuleCaptureHandler } from "@/lib/capture/types";
import { EMAIL_OPERATIONS, type EmailOperation } from "@/lib/emailReader";

const text = (value: unknown, max = 100_000) => typeof value === "string" ? value.trim().slice(0, max) : "";
const list = (value: unknown) => Array.isArray(value) ? value.filter((item): item is string => typeof item === "string").map((item) => item.trim()).filter(Boolean).slice(0, 20) : [];

export const emailCaptureHandler: ModuleCaptureHandler<EmailActionPrefill> = {
  tool: "propose_email_action",
  getRawText: (proposal) => {
    if (proposal.tool !== "propose_email_action") return "";
    const a = proposal.args as unknown as Record<string, unknown>;
    return JSON.stringify({
      action: text(a.action, 30), provider: text(a.provider, 30) || "auto",
      query: text(a.query, 500), message_id: text(a.message_id, 500), operation: text(a.operation, 30),
      to: list(a.to), cc: list(a.cc), bcc: list(a.bcc),
      subject: text(a.subject, 500), body_text: text(a.body_text, 200_000),
    });
  },
  extract: async (_rawText, proposal, ctx) => {
    if (proposal.tool !== "propose_email_action") return null;
    // Access is checked by the authenticated modal/backend, including the
    // validation cohort. Public catalog flags must not silently discard it.
    if (!ctx.ownerId || !ctx.sessionId) return null;
    const args = proposal.args as unknown as Record<string, unknown>;
    const action = ["search", "draft", "send", "reply", "organize"].includes(String(args.action))
      ? String(args.action) as EmailActionPrefill["action"]
      : "search";
    const provider = ["gmail", "outlook_mail"].includes(String(args.provider))
      ? String(args.provider) as "gmail" | "outlook_mail"
      : "auto";
    const messageId = text(args.message_id, 500);
    const operation = EMAIL_OPERATIONS.includes(args.operation as EmailOperation) ? args.operation as EmailOperation : undefined;
    if (action === "organize" && (provider === "auto" || typeof args.message_id !== "string" || args.message_id !== messageId || !messageId || /\s/.test(messageId) || !operation)) return null;
    return {
      action,
      provider,
      query: text(args.query, 500),
      to: list(args.to),
      cc: list(args.cc),
      bcc: list(args.bcc),
      subject: text(args.subject, 500),
      body_text: text(args.body_text, 200_000),
      action_id: proposal.action_id,
      expected_user_id: ctx.ownerId,
      expected_session_id: ctx.sessionId,
      message_id: messageId || undefined,
      ...(action === "organize" ? { operation } : {}),
    };
  },
  renderModal: ({ open, prefill, onClose, onCreated }) => (
    <EmailActionModal open={open} prefill={prefill} onClose={onClose} onCreated={onCreated} />
  ),
};
